/**
 * PowerSync Provider for SolidJS — centralizes database initialization
 * and shared connectivity orchestration.
 */

import type { AbstractPowerSyncDatabase } from "@powersync/common";
import {
  AgapePowerSyncConnector,
  connectivityMachine,
  createPowerSyncDatabase,
  createSelfUpdateController,
  handlePowerSyncAutoConnect,
  isWritePaused,
  pausesSync,
  readMaintenance,
  resolvePowerSyncUrl,
  startConnectivityManager,
  subscribeMaintenance,
  VISITOR_ID,
} from "@agape/shared";
import { type ActorRefFrom, createActor } from "xstate";
import { fromActorRef } from "@fleet/state-machines/solid";
import { sessionActor } from "@/lib/session-machine";
import { POWERSYNC_URL, SERVER_URL } from "@/lib/runtime-config";
import {
  type Accessor,
  batch,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  type ParentComponent,
  untrack,
  useContext,
} from "solid-js";
import { api } from "@/lib/api";
import { reportUpdateStalled, requestClientUpdate } from "@fleet/version";
import {
  readAccountsStateSync,
  readActiveAccountSelector,
  readActiveAccountSync,
} from "@/lib/accounts";
import { resolveSyncUserId, resolveTabAuthenticated } from "@/lib/active-account";
import { activeTabAccountId } from "@/lib/tab-account";
import { selectDatabaseFactory } from "@/lib/powersync/select-factory";
import { handleTauriPowerSyncAutoConnect, tauriDisconnect } from "@/lib/powersync/tauri-connect";
import { tokenStorage } from "@/lib/powersync/token-storage";
import { isTauriRuntime } from "@/lib/platform";
import { flushGuestScratch } from "@fleet/powersync";

// ─── PowerSync connector (web) ─────────────────────────
// Multi-account: each account caches its PowerSync JWT under its own namespace
// (the active user's id) so two accounts never clobber each other's cached
// credentials. The connector is rebuilt when the active user changes; the
// `fetchAuthToken` path uses the shared `api` client, which already attaches
// the ACTIVE account's Bearer token, so the fetched JWT belongs to whoever is
// active. A `null` namespace (no active user) keeps the legacy keys.
const POWERSYNC_ENDPOINT = resolvePowerSyncUrl({
  isWeb: true,
  isDev: import.meta.env.DEV,
  serverUrl: SERVER_URL,
  powerSyncUrl: POWERSYNC_URL,
  location: {
    hostname: globalThis.window.location.hostname,
    protocol: globalThis.window.location.protocol,
    origin: globalThis.window.location.origin,
  },
});

function createWebConnector(userId: string | null): AgapePowerSyncConnector {
  const ownKey = userId && userId !== VISITOR_ID ? userId : VISITOR_ID;
  return new AgapePowerSyncConnector({
    endpoint: POWERSYNC_ENDPOINT,
    storage: tokenStorage,
    apiClient: api,
    cacheNamespace: userId && userId !== VISITOR_ID ? userId : undefined,
    // CC-42 upload gate: `uploadBatch` posts through the SHARED `api` client,
    // whose per-request identity is the tab's ACTIVE account (`X-Active-Account`
    // selector on web). A non-active account's DB can still flush its queued
    // CRUD (initial-sync upload throttle + account switch), which the server
    // would authenticate as the OTHER account and silently drop (`user_op`) or
    // misattribute — permanent data loss. Defer instead: ops stay queued and
    // are re-triggered when this account becomes active again (see
    // `loadDbForUser`). On Tauri the sync loop lives in Rust (this JS uploader
    // is not the upload path), so the gate stays open there.
    // Maintenance gate: while the server's window pauses writes, DEFER instead
    // of uploading. The 503 the server returns is only the safety net; not
    // sending is what keeps the queue intact and the window invisible to the
    // user (reads and downloads keep working off the local DB). Uploads resume
    // by themselves — the health poll flips this back and PowerSync retries.
    canUploadNow: () =>
      !isWritePaused() &&
      (isTauriRuntime() || (readActiveAccountSelector() ?? VISITOR_ID) === ownKey),
    // PER-CONNECTOR (not global): a connector fetches a token ONLY for its OWN
    // real account. The guest connector (`userId === null`) NEVER fetches/syncs —
    // even while a real account stays globally signed-in in another tab — so a
    // guest tab is strictly local-only and its local rows never get uploaded/
    // cleared. The `api` client attaches the active tab account's Bearer, so the
    // fetched JWT belongs to this connector's account.
    shouldFetchToken: () => userId !== null && userId !== VISITOR_ID,
    onAuthError: (error) => {
      console.warn("[PowerSync] Auth failed (401/403). Notifying state machine.");
      sessionActor.send({
        type: "SERVER_REPLY",
        session: null,
        error,
      });
    },
  });
}

interface PowerSyncContextValue {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  isLoading: Accessor<boolean>;
  error: Accessor<Error | null>;
  disconnectAndClear: () => Promise<void>;
  /**
   * Destroys + forgets a specific account's local database (used by
   * remove-account in the switcher). Unlike `disconnectAndClear`, the account
   * need not be the active one.
   */
  destroyAccountData: (userId: string) => Promise<void>;
  /**
   * Flushes the guest DB's freshly-merged rows to the server as `userId` so the
   * real account's per-user DB can sync them (guest→real upgrade). No-op if
   * there is no guest DB instance.
   */
  flushGuestUpgrade: (userId: string) => Promise<void>;
  connectivityRef: ActorRefFrom<typeof connectivityMachine>;
}

const PowerSyncContext = createContext<PowerSyncContextValue>();

// Multi-account: one PowerSync database instance PER user id (each backed by
// its own on-disk DB file via `dbFilenameForUser`). Switching accounts swaps
// the active instance WITHOUT clearing any data — only a real sign-out /
// remove-account destroys an instance. The guest/visitor uses VISITOR_ID.
interface DbInstance {
  db: AbstractPowerSyncDatabase;
  connector: AgapePowerSyncConnector;
}

const dbInstances = new Map<string, DbInstance>();
const dbInstancePromises = new Map<string, Promise<DbInstance>>();

function userKey(userId: string | null | undefined): string {
  return userId && userId !== VISITOR_ID ? userId : VISITOR_ID;
}

// Exported as the test seam for the rejected-init retry (guest-db-rebind.test.ts).
export async function getOrCreateDbForUser(userId: string | null): Promise<DbInstance> {
  const key = userKey(userId);
  const existing = dbInstances.get(key);
  if (existing) {
    return existing;
  }

  let pending = dbInstancePromises.get(key);
  if (!pending) {
    pending = (async () => {
      const isDev = import.meta.env.DEV;
      const factory = await selectDatabaseFactory();
      const database = await createPowerSyncDatabase(factory, {
        isDev,
        userId: key === VISITOR_ID ? null : key,
      });
      await database.init();
      const instance: DbInstance = {
        db: database,
        connector: createWebConnector(key === VISITOR_ID ? null : key),
      };
      dbInstances.set(key, instance);
      return instance;
    })();
    // CRITICAL: the in-flight promise must NOT outlive its own settlement in the
    // cache. We keep it ONLY while pending (to dedupe concurrent callers); the
    // resolved instance lives in `dbInstances` (the fast path above), and on
    // FAILURE the slot must be released so a later effect re-run can retry.
    // Otherwise a transient `init()` failure (e.g. the SHARED sync worker being
    // torn down by a sibling DB's `disconnectAndClear()` during a real-account
    // sign-out, which races a concurrent guest-DB (re)build) would cache a
    // REJECTED promise forever: every subsequent `getOrCreateDbForUser(null)`
    // re-returns the same rejection, the guest DB never settles, and the app
    // hangs on the boot shell after switching back to the guest. Clearing the
    // pending entry on settle (both paths) makes the rebuild self-healing.
    const tracked = pending;
    // `.then(release, release)` rather than `.finally`: `finally` re-throws the
    // rejection on a derived promise nobody awaits, which surfaces as an
    // unhandled rejection on every failed build.
    const release = () => {
      if (dbInstancePromises.get(key) === tracked) {
        dbInstancePromises.delete(key);
      }
    };
    void tracked.then(release, release);
    dbInstancePromises.set(key, pending);
  }

  return pending;
}

/** Destroys + forgets a user's DB instance (real sign-out / remove account). */
async function destroyDbForUser(userId: string | null): Promise<void> {
  const key = userKey(userId);
  const instance = dbInstances.get(key);
  dbInstances.delete(key);
  dbInstancePromises.delete(key);
  if (!instance) {
    return;
  }
  try {
    await instance.connector.clearTokenCache();
    await instance.db.disconnectAndClear();
  } catch (err) {
    console.warn("[PowerSyncProvider] destroyDbForUser failed:", err);
  }
}

/**
 * Guest → real upgrade (merge): after `sweepVisitorData` re-stamps the guest's
 * rows + queued CRUD from `VISITOR_ID` to the real user in the guest DB
 * (`app.db`), those writes are stranded — the app is about to swap the active
 * DB to the real account's own file (`app-<userId>.db`), and the guest DB is
 * never the active/connected DB so it would never upload.
 *
 * This flushes them via the CLOUD (the source of truth per user): connect the
 * guest DB with a connector authenticated as the real account, wait for its
 * upload queue to drain to the server, then disconnect. The real account's DB
 * then downloads the merged rows on its normal initial sync — and they survive a
 * full reload, because the data now lives server-side, not just in `app.db`.
 *
 * GUEST-DATA LIFECYCLE (no duplication): once the upload has drained, the data
 * lives in the real account (cloud + its own per-user DB). The guest scratch
 * (`app.db`) must therefore be CLEARED — otherwise the same rows linger in
 * the guest entry and a later "Continue as guest" would still show them, which
 * is the user-reported duplication bug. We `disconnectAndClear()` the guest DB
 * and forget its instance so the guest entry stays available as a FRESH EMPTY
 * scratch. This runs on BOTH the register auto-claim and the login merge paths
 * (both call `flushGuestUpgrade`).
 */
async function flushGuestDataToServer(realUserId: string): Promise<void> {
  const guest = dbInstances.get(VISITOR_ID);
  if (!guest) {
    return;
  }
  // A connector namespaced to the real user so the fetched PowerSync JWT (whose
  // `sub` is the real user, via the now-active real Bearer) is cached correctly.
  // NOTE: we must NOT reuse the standard connector here — its `shouldFetchToken`
  // gates on the session machine, which has NOT yet been primed as the real user
  // at this point in the upgrade, so it would refuse to fetch a token and the
  // connect would silently fail (connected=false, nothing uploads). This
  // dedicated connector always fetches: we KNOW the real account's Bearer is the
  // active one (it was just registered) and that we are authenticated.
  const connector = new AgapePowerSyncConnector({
    endpoint: POWERSYNC_ENDPOINT,
    storage: tokenStorage,
    apiClient: api,
    cacheNamespace: realUserId,
  });
  try {
    await guest.db.connect(connector);
  } catch (err) {
    // Not fatal: the writes stay queued, and the flush below keeps them.
    console.warn("[PowerSyncProvider] guest connect failed:", err);
  }
  // The scratch is cleared only once its upload queue is empty; otherwise its
  // rows stay put and the next sign-in upgrade retries them (`@fleet/powersync`).
  const outcome = await flushGuestScratch({
    pendingCount: async () => {
      // oxlint-disable-next-line lingui/no-unlocalized-strings -- SQL query
      const rows = await guest.db.getAll<{ n: number }>("SELECT count(*) AS n FROM ps_crud");
      return rows[0]?.n ?? -1;
    },
    clear: () => guest.db.disconnectAndClear(),
    disconnect: () => guest.db.disconnect(),
  });
  if (outcome === "kept") {
    console.error("[PowerSyncProvider] guest writes did not upload; kept for the next sign-in");
    return;
  }
  dbInstances.delete(VISITOR_ID);
  dbInstancePromises.delete(VISITOR_ID);
}

export const PowerSyncProvider: ParentComponent = (props) => {
  const [db, setDb] = createSignal<AbstractPowerSyncDatabase | null>(null);
  const [isLoading, setIsLoading] = createSignal<boolean>(true);
  const [syncError, setSyncError] = createSignal<Error | null>(null);
  // The connector bound to the currently-active DB instance. Swapped on account
  // switch so the auto-connect effect always uses the active account's connector.
  const [activeConnector, setActiveConnector] = createSignal<AgapePowerSyncConnector | null>(null);
  // The account the bound DB and connector were made for. It changes in the same
  // batch as `db`, so it tells a stale guest DB apart from the account's own DB.
  const [boundUserId, setBoundUserId] = createSignal<string | null>(null);

  // 1. Reactive signals of the session actor
  const snapshot = fromActorRef(sessionActor);
  const currentSession = () => snapshot().context.session;
  const isVisitor = () => snapshot().context.isVisitor;
  const isPriming = () => snapshot().matches("priming");

  // 1b. REACTIVE per-tab active account ← router `/u/<n>`.
  //
  // The per-tab active account is encoded in the URL path prefix `/u/<n>` (see
  // `lib/active-account.ts`). An IN-APP account switch (the switcher) navigates
  // to a new `/u/<m>` WITHOUT a reload — the URL changes but no session signal
  // necessarily changes the way the DB-binding effect tracks (and a synchronous
  // `globalThis.location.pathname` read is NOT reactive), so the effect that
  // binds the per-user PowerSync DB would not re-run and the displayed data
  // would not swap until a hard refresh. To make the switch reactive we mirror
  // the router's current pathname into a Solid signal here.
  //
  // The provider is mounted ABOVE `<RouterProvider>`, so the `useLocation()`
  // hook (which needs the router *context*) is unavailable, and importing the
  // `@/router` singleton to subscribe to its lifecycle creates a massive import
  // cycle (router → routes → components/providers → router). Instead we track
  // the browser History API directly (router-agnostic) — see
  // `lib/reactive-location.ts`. This catches every navigation (SPA push/replace
  // + back/forward), so the DB-binding effect re-runs on an in-app switch.

  // 2. Initialize the connectivity machine and the shared orchestrator
  const connectivityRef = createActor(connectivityMachine).start();

  // API contract compatibility. The whole point is that the user never learns
  // any of this exists: a build below the server's `min_supported` updates
  // itself, and a build ahead of the server (the normal state of every deploy,
  // since Pages publishes before the k8s rollout lands) is left alone.
  //
  // `localStorage` is read lazily and defensively: some privacy modes throw on
  // ACCESS, not just on write, and that must not take the provider down. No
  // storage means the guard cannot count its attempts, and a guard that cannot
  // count must not reload — see `@agape/shared`'s `self-update`.
  const selfUpdate = createSelfUpdateController({
    clientVersion: import.meta.env.API_CONTRACT_VERSION,
    storage: (() => {
      try {
        return globalThis.localStorage;
      } catch {
        return null;
      }
    })(),
    triggerUpdate: requestClientUpdate,
    onGiveUp: (info) => {
      console.warn(
        `[api-contract] client v${info.clientVersion} is below the server minimum v${info.minSupported} ` +
          `and did not manage to update itself in ${info.attempts} attempts`,
      );
      reportUpdateStalled();
    },
  });

  const manager = startConnectivityManager({
    getDb: db,
    apiClient: api,
    connectivityRef,
    clientContractVersion: import.meta.env.API_CONTRACT_VERSION,
    onContractVerdict: (verdict) => {
      if (verdict.kind === "client-outdated") {
        selfUpdate.handleOutdated(verdict.minSupported);
      } else if (verdict.kind === "compatible") {
        selfUpdate.markCompatible();
      }
      // `client-ahead` and `unknown` are deliberately no-ops: the first is
      // transitory by construction, the second means we know nothing.
    },
    onClientAheadPersists: (info) => {
      // Not user-facing on purpose. A front that stays ahead of the API past a
      // rollout window is an OPERATOR problem (a stuck k8s rollout), not
      // something the user can act on, and the app keeps working meanwhile.
      console.warn(
        `[api-contract] client v${info.clientVersion} has been ahead of server v${info.serverCurrent} ` +
          `for ${Math.round(info.sinceMs / 1000)}s — is the API rollout stuck?`,
      );
    },
    // NOTE: intentionally no `onInvalidateQueries`. Real-time freshness is owned
    // entirely by each query's PowerSync subscription (`onChangeWithCallback` +
    // `triggerImmediate` in `createPowerSyncQuery`), which refetches only the
    // queries whose own tables changed — granular and identical on every
    // platform (the @powersync/tauri-plugin forwards table-change events to JS).
    // A blanket `invalidateQueries()` here refetched *everything* on every sync
    // settle, which is what flashed the theme/background after a mutation and is
    // needlessly heavy on mobile. Screens that need a guaranteed-fresh pull on
    // entry should invalidate their own keys via TanStack at that point.
  });

  onCleanup(() => {
    manager.dispose();
  });

  // Server maintenance window, mirrored into a Solid signal so the auto-connect
  // effect re-runs when it opens AND when it closes (that second half is what
  // makes the window pass on its own, with no user action).
  const [maintenance, setMaintenanceSignal] = createSignal(readMaintenance());
  onCleanup(
    subscribeMaintenance((window) => {
      setMaintenanceSignal(window);
    }),
  );

  // Periodic API health check (managed by the manager)
  createEffect(() => {
    if (import.meta !== undefined && import.meta.env?.SSR) {
      return;
    }
    if (navigator.onLine) {
      void manager.startHealthCheck();
    } else {
      manager.stopHealthCheck();
    }
  });

  // Foreground re-probe (mobile PWA bug): when the tab returns to the
  // foreground — app switch (`visibilitychange`), restore from iOS Safari's
  // bfcache (`pageshow`) or window focus (`focus`) — the mobile browser has
  // suspended/throttled the health-check interval and any in-flight ping may
  // have failed, leaving `apiStatus="down"`. Since the network did not really
  // change, no `online` event fires, so nothing forced a re-check and the
  // "server not responding" banner stayed stuck until a manual refresh. Here
  // we re-sync the network state and force an immediate API probe so the
  // connectivity machine goes back to "up" WITHOUT reloading the page.
  createEffect(() => {
    const handleForeground = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      manager.handlers.onNetworkChanged(navigator.onLine);
      void manager.forceHealthCheck();
    };

    document.addEventListener("visibilitychange", handleForeground);
    globalThis.addEventListener("pageshow", handleForeground);
    globalThis.addEventListener("focus", handleForeground);

    onCleanup(() => {
      document.removeEventListener("visibilitychange", handleForeground);
      globalThis.removeEventListener("pageshow", handleForeground);
      globalThis.removeEventListener("focus", handleForeground);
    });
  });

  // Listener for the browser's network state
  createEffect(() => {
    const handleOnline = () => {
      manager.handlers.onNetworkChanged(true);
    };
    const handleOffline = () => {
      manager.handlers.onNetworkChanged(false);
    };

    globalThis.addEventListener("online", handleOnline);
    globalThis.addEventListener("offline", handleOffline);

    // Initial state
    manager.handlers.onNetworkChanged(navigator.onLine);

    onCleanup(() => {
      globalThis.removeEventListener("online", handleOnline);
      globalThis.removeEventListener("offline", handleOffline);
    });
  });

  // Listener for the authentication state.
  //
  // PER-TAB coherence: the connectivity machine (and therefore the sync pill)
  // must reflect THIS tab's ACTIVE account — coherent with
  // `useSession().isAuthenticated()` — NOT only the GLOBAL session machine.
  // Gating on `currentSession()?.user` meant that when the active per-tab
  // account is a REAL account but the global machine is not (yet) primed for it
  // (the minimal-view window right after a login / account switch, or a real
  // account open only in another tab), `onAuthChanged(false)` drove the machine
  // to `unauthenticated` → the pill showed "Visitor"/"Unauthenticated" and sync
  // never connected, even though the Profile correctly showed the user logged in.
  // `activeUserId()` resolves the per-tab account (URL `/u/<n>` → registry), so a
  // real active account reports authenticated regardless of global priming.
  createEffect(() => {
    manager.handlers.onAuthChanged(activeAuthenticated());
  });

  // Monitor PowerSync's real state (managed by the manager)
  createEffect(() => {
    const database = db();
    if (database) {
      manager.setupDbListener(database);
    }
  });

  // Auto-connect to PowerSync (managed by shared logic)
  // The session signals (currentSession, isVisitor, isPriming) are read
  // reactively so the effect re-runs when the session changes (e.g. login).
  // isConnecting is read with untrack() so connection failures (e.g. 429
  // rate-limit) do not re-trigger the effect in an infinite loop.
  const [isConnecting, setIsConnecting] = createSignal(false);
  const onTauri = isTauriRuntime();
  createEffect(() => {
    const database = db();
    if (!database) {
      return;
    }

    // PER-TAB override (boot-safe layered, mirrors useSession): a GUEST active
    // tab is strictly local-only — force visitor=true / authenticated=false so
    // the effect NEVER connects the guest DB, even while a real account stays
    // globally signed-in in another tab (otherwise the guest's local rows would
    // be uploaded/cleared and a new guest task would vanish). A real active tab
    // keeps the baseline global behavior (connect with its own connector).
    const activeIsGuest = activeUserId() === VISITOR_ID;
    // PER-TAB authenticated (coherent with `useSession().isAuthenticated()`): a
    // REAL active account enables the connect with ITS OWN connector — even when
    // the GLOBAL session machine is not primed for this tab (post-login/switch
    // minimal-view, or the account being open only in another tab). The previous
    // `Boolean(currentSession()?.user)` gated on the global session, so a real
    // per-tab account whose global machine wasn't primed never connected → the
    // pill stuck on "unauthenticated" with no sync. The guest case stays
    // local-only: `activeIsGuest` forces `authenticated=false` (its connector
    // also refuses to fetch a token), and sign-out drops the account from the
    // registry so `activeUserId()` falls back to the visitor before we navigate.
    const authenticated = activeAuthenticated();
    // Registration flips the session to the new account while `db()` still holds
    // the guest DB until the account's own DB is bound. Connecting that guest DB
    // with the account's session stalls and leaves `isConnecting` set, which then
    // blocks the account's DB from connecting. Wait for the bind instead.
    if (authenticated && boundUserId() !== activeUserId()) {
      return;
    }
    const visitor = activeIsGuest || isVisitor();
    const priming = isPriming();

    // Silently skip auto-connect if we don't have a reason to be connected.
    // This prevents the "Visitor or no auth detected" warning during logout.
    if (!authenticated && !priming && !visitor) {
      return;
    }

    if (onTauri) {
      // The Tauri PowerSync SDK forbids `db.connect()` from JS — start the
      // sync loop in Rust instead. JS fetches the JWT here (cookies live
      // on the backend's origin, invisible to `document.cookie` from
      // `tauri.localhost`) and pushes it to Rust via the
      // `powersync_connect` / `powersync_update_token` commands.
      void handleTauriPowerSyncAutoConnect({
        database,
        apiClient: api,
        apiBase: SERVER_URL,
        powerSyncEndpoint: POWERSYNC_URL,
        isAuthenticated: authenticated,
        isVisitor: visitor,
        isPriming: priming,
        isConnecting: untrack(isConnecting),
        setIsConnecting,
        isMaintenancePaused: pausesSync(maintenance()),
      });
      return;
    }

    const connector = activeConnector();
    if (!connector) {
      return;
    }
    void handlePowerSyncAutoConnect({
      database,
      connector,
      isAuthenticated: authenticated,
      isVisitor: visitor,
      isPriming: priming,
      isConnecting: untrack(isConnecting),
      setIsConnecting,
      // Only `sync`/`all` disconnects. A `writes` window leaves the connection
      // up — downloads keep flowing and only the upload queue holds.
      isMaintenancePaused: pausesSync(maintenance()),
    });
  });

  // ── Active-user → DB instance binding ──────────────────────────────────────
  // The active session's user id selects which per-user DB instance is exposed
  // by the context. When it changes (initial prime OR an account switch) we
  // load/create that user's instance and swap it in WITHOUT clearing anything.
  //
  // Multi-account boot determinism: while the session machine is still
  // `priming` (its `primeSession` actor reads localStorage asynchronously),
  // `currentSession()` is transiently `null`. Falling back to `VISITOR_ID`
  // here would (a) spin up a throwaway visitor DB on every boot of a real
  // account and (b) make `activeUserId` flip VISITOR→real once priming lands,
  // thrashing the per-user DB instances. Instead, during priming we read the
  // persisted active account synchronously so the FIRST (and only) DB created
  // for a remembered real account is that account's DB — no visitor detour,
  // no flip.
  // The decision lives in `resolveSyncUserId` / `resolveTabAuthenticated`
  // (lib/active-account.ts), so the unit test runs the code that binds the DB.
  const syncInputs = () => ({
    state: readAccountsStateSync(),
    tabAccountId: activeTabAccountId(),
    sessionUserId: currentSession()?.user.id ?? null,
    priming: isPriming(),
    persisted: isPriming() ? readActiveAccountSync() : null,
  });
  const activeUserId = () => resolveSyncUserId(syncInputs());
  // PER-TAB authenticated view (mirrors `useSession().isAuthenticated()`): a real,
  // non-guest account. Used by BOTH the connectivity bridge (`onAuthChanged`) and
  // the auto-connect effect so they can never disagree about whether to sync.
  const activeAuthenticated = () => resolveTabAuthenticated(syncInputs());

  let loadToken = 0;
  // Bounded auto-retry for a TRANSIENT DB (re)build failure (the slot is already
  // released on rejection by `getOrCreateDbForUser`). Without this, a one-off
  // `init()` failure — e.g. the shared sync worker briefly torn down by a sibling
  // DB's `disconnectAndClear()` during a real-account sign-out, racing the
  // guest-DB rebuild — would leave `db()` null forever (the binding effect won't
  // re-run on its own, since `activeUserId()` did not change), hanging the boot
  // shell after switching back to the guest.
  const MAX_DB_LOAD_RETRIES = 3;
  const DB_LOAD_RETRY_MS = 250;
  const loadDbForUser = (userId: string, attempt = 0) => {
    loadToken += 1;
    const token = loadToken;
    setIsLoading(true);
    void (async () => {
      try {
        const instance = await getOrCreateDbForUser(userId === VISITOR_ID ? null : userId);
        if (token !== loadToken) {
          return; // a newer switch superseded this load
        }
        // CC-29: bind the connector + DB ATOMICALLY. These are two separate
        // signals; written one after another (this runs in an async `.then`,
        // outside any Solid batch) the auto-connect effect fires after the
        // FIRST write and observes a TORN pair — the new account's connector
        // paired with the OLD, about-to-be-retired guest DB. On a fresh
        // registration that mixed run connected the GUEST DB with the real
        // account's connector (cross-account contamination) and set the shared
        // `isConnecting` flag true; when the effect re-ran with the coherent
        // (real DB + real connector) pair, `isConnecting` was still true (and
        // read via `untrack`, so its later reset never re-triggered the effect)
        // → the real account's DB never connected and sync-DOWN stayed dead
        // until a full reload. `batch` guarantees observers only ever see the
        // fully-swapped, coherent state, so the effect connects the RIGHT DB
        // with the RIGHT connector on its first (and only) run. INVARIANT: does
        // NOT touch disconnectAndClear/flushGuestUpgrade/destroyDbForUser —
        // batch only makes the bind atomic; it cannot delete data.
        batch(() => {
          setBoundUserId(userId);
          setActiveConnector(instance.connector);
          setDb(instance.db);
          setSyncError(null);
          setIsLoading(false);
        });
        if (import.meta.env.DEV) {
          // DEV-ONLY hook for the isolation e2e tests (permissions-leak):
          // lets Playwright query the local SQLite and prove that rows from
          // other scopes NEVER reached the device (the UI hiding them is not
          // enough). Not registered in production.
          Reflect.set(globalThis, "__agapeDbQuery", (sql: string, params: unknown[] = []) =>
            instance.db.getAll(sql, params),
          );
        }
        // CC-42: this account just became the tab's ACTIVE identity. Any CRUD
        // it queued while another account was active was DEFERRED by the
        // connector's upload gate (see `createWebConnector`), and if its sync
        // stream stayed connected across the switch there is no reconnect to
        // flush it — so nudge the upload queue explicitly now.
        try {
          instance.db.syncStreamImplementation?.triggerCrudUpload();
        } catch {
          // Not connected yet — the upcoming (re)connect flushes the queue.
        }
      } catch (caught) {
        if (token !== loadToken) {
          return;
        }
        const err = caught instanceof Error ? caught : new Error(String(caught));
        console.error("[PowerSyncProvider] init error:", err);
        if (attempt < MAX_DB_LOAD_RETRIES) {
          // Re-attempt the SAME user; the poisoned promise slot was cleared on
          // rejection, so this rebuilds from scratch and self-heals.
          setTimeout(() => {
            if (token === loadToken) {
              loadDbForUser(userId, attempt + 1);
            }
          }, DB_LOAD_RETRY_MS);
          return;
        }
        setSyncError(err);
        setIsLoading(false);
      }
    })();
  };

  // React to active-user changes (drives both initial boot and account switch).
  let lastUserKey: string | null = null;
  createEffect(() => {
    const uid = activeUserId();
    const key = userKey(uid);
    if (key === lastUserKey) {
      return;
    }
    lastUserKey = key;
    loadDbForUser(uid);
  });

  const disconnectAndClear = async () => {
    const database = db();
    const connector = activeConnector();
    if (!database) {
      return;
    }
    const start = Date.now();
    console.info("[PowerSyncProvider] Starting database cleanup...");
    try {
      // 1. Explicitly pause sync first to avoid contention during cleanup.
      //    On Tauri, the sync runs in Rust — disconnect there too.
      await (onTauri ? tauriDisconnect(database) : database.disconnect());

      // 2. Clear local token cache
      await connector?.clearTokenCache();

      // 3. Wipe the database
      await database.disconnectAndClear();

      // 4. Forget this user's instance so a future re-login rebuilds it fresh.
      const key = lastUserKey ?? VISITOR_ID;
      dbInstances.delete(key);
      dbInstancePromises.delete(key);

      console.info(`[PowerSyncProvider] Database cleanup completed in ${Date.now() - start}ms`);
    } catch (err) {
      console.error("[PowerSyncProvider] Failed to clear database:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const value: PowerSyncContextValue = {
    db,
    isLoading,
    error: syncError,
    disconnectAndClear,
    destroyAccountData: async (userId: string) => destroyDbForUser(userId),
    flushGuestUpgrade: async (userId: string) => flushGuestDataToServer(userId),
    connectivityRef,
  };

  return <PowerSyncContext.Provider value={value}>{props.children}</PowerSyncContext.Provider>;
};

/**
 * Hook (signal factory) to access the PowerSyncContext.
 * Throws if used outside the provider.
 */
export function usePowerSyncContext(): PowerSyncContextValue {
  const ctx = useContext(PowerSyncContext);
  if (!ctx) {
    throw new Error("[usePowerSyncContext] used outside <PowerSyncProvider>");
  }
  return ctx;
}
